/**
 * Display helpers for UAE dates. The backend stores UTC instants (e.g. an erasure date is
 * 00:00 UTC on its day); Athlyn staff read them as calendar dates in the UAE (Asia/Dubai, UTC+4,
 * no daylight saving). Missing or unparseable values render as an em dash.
 */
const DUBAI_TZ = "Asia/Dubai";

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? "";
}

/** "10 Nov 2026" — the calendar date in Asia/Dubai. */
export function formatDubaiDate(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DUBAI_TZ,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).formatToParts(d);
  return `${part(parts, "day")} ${part(parts, "month")} ${part(parts, "year")}`;
}

/** "10 Nov 2026, 10:05" — date and 24-hour time in Asia/Dubai. */
export function formatDubaiDateTime(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DUBAI_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  return `${formatDubaiDate(value)}, ${part(parts, "hour")}:${part(parts, "minute")}`;
}

/**
 * Dubai is a fixed UTC+4 (no daylight saving), so local ⇄ UTC is plain arithmetic — no
 * `date-fns-tz`, the same choice as the server (splash-server). Used by the Splash screens page,
 * which takes dates + times in Dubai time and sends UTC ISO strings.
 */
const DUBAI_OFFSET_MS = 4 * 60 * 60 * 1000;

/** Default start / end time of a new splash's dates (SP-D13). */
export const DEFAULT_SPLASH_START_TIME = "00:00";
export const DEFAULT_SPLASH_END_TIME = "23:59";

/**
 * A Dubai date (`yyyy-MM-dd`) + time (`HH:mm`) as a UTC ISO string, e.g. `("2026-12-01", "00:00")`
 * ⇒ `"2026-11-30T20:00:00.000Z"`. `null` for a missing or impossible date or time.
 */
export function dubaiToUtcIso(date: string, time: string): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !t) return null;
  const [year, month, day, hour, minute] = [d[1], d[2], d[3], t[1], t[2]].map(Number);
  if (hour > 23 || minute > 59) return null;
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  const check = new Date(asUtc);
  // Date.UTC rolls an impossible day (30 Feb) into the next month: refuse it instead.
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  return new Date(asUtc - DUBAI_OFFSET_MS).toISOString();
}

const pad = (n: number) => String(n).padStart(2, "0");

/** A UTC instant as its Dubai date (`yyyy-MM-dd`) and time (`HH:mm`) — the edit form's fields. */
export function utcToDubaiParts(value: string | null | undefined): { date: string; time: string } {
  const d = toDate(value);
  if (!d) return { date: "", time: "" };
  const local = new Date(d.getTime() + DUBAI_OFFSET_MS);
  return {
    date: `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}`,
    time: `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`,
  };
}

/** The Dubai calendar day (`yyyy-MM-dd`) of an instant. */
export function dubaiDayKey(at: Date): string {
  return utcToDubaiParts(at.toISOString()).date;
}
